import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import Cookies from "js-cookie";
import "./ContactInfo.css";
import "../../components/header/userMenu/SettingsPrivacy.css";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

const BackIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const XIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const ChevronLeftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const EmailIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);

export default function AccountOwnershipPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state);

  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEmailAccountModal, setShowEmailAccountModal] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [showDeleteEmailModal, setShowDeleteEmailModal] = useState(false);
  const [isDeletingEmail, setIsDeletingEmail] = useState(false);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (user && user.token) {
        try {
          const res = await axios.get(`${API_URL}/getProfile/${user.username}`, {
            headers: { Authorization: `Bearer ${user.token}` },
          });
          if (res.data && (res.data.user || res.data.email || res.data._id)) {
            const profileData = res.data.user || res.data;
            setUserProfile(profileData);
          } else {
            setUserProfile(user);
          }
        } catch (err) {
          console.error("Failed to fetch user profile:", err);
          setUserProfile(user);
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };
    fetchUserProfile();
  }, [user]);

  const handleBack = () => {
    if (location.state?.from) {
      navigate(location.state.from);
    } else {
      navigate(-1);
    }
  };

  const handleDeleteEmail = async () => {
    if (!selectedEmail) return;

    setIsDeletingEmail(true);
    try {
      const res = await axios.delete(
        `${API_URL}/deleteEmail`,
        {
          data: { email: selectedEmail },
          headers: { Authorization: `Bearer ${user.token}` },
        }
      );

      if (res.data && res.data.user) {
        setUserProfile(res.data.user);
        setShowDeleteEmailModal(false);
        setShowEmailAccountModal(false);
        setSelectedEmail(null);
      }
    } catch (err) {
      alert(err.response?.data?.message || "Failed to delete email. Please try again.");
    } finally {
      setIsDeletingEmail(false);
    }
  };

  const isAdditionalEmail = () => {
    if (!selectedEmail || !userProfile) return false;
    return userProfile.additionalEmails && userProfile.additionalEmails.includes(selectedEmail);
  };

  const handleDeleteAccount = async () => {
    setIsDeletingAccount(true);
    try {
      // Validate token exists
      if (!user || !user.token) {
        throw new Error("Authentication token is missing. Please log in again.");
      }

      const res = await axios.delete(
        `${API_URL}/deleteAccount`,
        {
          headers: { 
            Authorization: `Bearer ${user.token}`,
            "Content-Type": "application/json"
          },
        }
      );

      if (res.data && res.data.success) {
        Cookies.remove("user");
        localStorage.removeItem("user");
        sessionStorage.clear();
        dispatch({ type: "LOGOUT" });
        setShowDeleteAccountModal(false);
        navigate("/login", { replace: true });
        return;
      }

      throw new Error(res.data?.message || "Failed to delete account.");
    } catch (err) {
      console.error("Failed to delete account:", err);
      const errorMessage = err.response?.data?.message || err.message || "Failed to delete account. Please try again.";
      alert(errorMessage);
      
      // If authentication failed, redirect to login
      if (err.response?.status === 400 && err.response?.data?.message?.includes("Authentification")) {
        Cookies.remove("user");
        localStorage.removeItem("user");
        dispatch({ type: "LOGOUT" });
        navigate("/login", { replace: true });
      }
    } finally {
      setIsDeletingAccount(false);
    }
  };

  const renderEmailItem = (email) => (
    <div
      key={email}
      className="contact_info_item"
      onClick={() => {
        setSelectedEmail(email);
        setShowEmailAccountModal(true);
      }}
      style={{ cursor: "pointer" }}
    >
      <div className="contact_info_icon">
        <EmailIcon />
      </div>
      <div className="contact_info_text">
        <span className="contact_info_value">{email}</span>
      </div>
      <div className="contact_info_arrow">
        <ChevronRightIcon />
      </div>
    </div>
  );

  return (
    <div className="contact_info_page">
      <header className="contact_info_page_header">
        <button className="contact_info_page_back" onClick={handleBack} aria-label="Go back">
          <BackIcon />
        </button>
        <h1>Account ownership and control</h1>
      </header>

      <main className="contact_info_page_body">
        {loading ? (
          <div className="contact_info_loading">Loading...</div>
        ) : (
          <div className="contact_info_page_card">
            <p className="contact_info_page_description">
              Manage your data, modify your legacy contact, and deactivate or delete your accounts and profiles.
            </p>

            <div className="contact_info_list">
              {userProfile?.email && renderEmailItem(userProfile.email)}

              {userProfile?.additionalEmails &&
                userProfile.additionalEmails.length > 0 &&
                userProfile.additionalEmails.map((email) => renderEmailItem(email))}
            </div>
          </div>
        )}
      </main>

      {/* Email Account Information Modal */}
      {showEmailAccountModal && selectedEmail && (
        <div
          className="contact_info_modal_overlay"
          onClick={() => {
            setShowEmailAccountModal(false);
            setSelectedEmail(null);
          }}
        >
          <div className="contact_info_modal email_account_modal" onClick={(e) => e.stopPropagation()}>
            <div className="email_account_modal_header">
              <button
                className="email_account_back_btn"
                onClick={() => {
                  setShowEmailAccountModal(false);
                  setSelectedEmail(null);
                }}
                aria-label="Go Back"
              >
                <ChevronLeftIcon />
              </button>
              <h2 className="email_account_modal_title">{selectedEmail}</h2>
              <button
                className="contact_info_modal_close"
                onClick={() => {
                  setShowEmailAccountModal(false);
                  setSelectedEmail(null);
                }}
                aria-label="Close"
              >
                <XIcon />
              </button>
            </div>

            <div className="contact_info_modal_content">
              <p className="email_account_info_text">
                You added this email to these accounts.{" "}
                <span className="email_account_privacy_link"></span>
              </p>

              <div className="email_account_linked_section">
                <div className="linked_account_item linked_account_item_current">
                  <div className="linked_account_avatar">
                    <img
                      src={userProfile?.picture || user?.picture || "/default-avatar.png"}
                      alt={userProfile?.name || user?.name || "Profile"}
                      onError={(e) => {
                        e.target.src = "/default-avatar.png";
                      }}
                    />
                  </div>
                  <div className="linked_account_info">
                    <div className="linked_account_name">
                      {userProfile?.name ||
                        user?.name ||
                        (userProfile?.first_name && userProfile?.last_name
                          ? `${userProfile.first_name} ${userProfile.last_name}`
                          : userProfile?.first_name || userProfile?.last_name || "User")}
                    </div>
                  </div>
                  {/* <div className="contact_info_arrow">
                    <ChevronRightIcon />
                  </div> */}
                </div>
              </div>

              {isAdditionalEmail() && (
                <button
                  className="email_account_delete_btn email_delete_btn"
                  onClick={() => {
                    setShowDeleteEmailModal(true);
                  }}
                  style={{ marginBottom: "12px" }}
                >
                  Delete email
                </button>
              )}

              <button
                className="email_account_delete_btn"
                onClick={() => {
                  setShowDeleteAccountModal(true);
                }}
              >
                Delete account
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Email Confirmation Modal */}
      {showDeleteEmailModal && selectedEmail && (
        <div
          className="delete_account_modal_overlay"
          onClick={() => {
            if (!isDeletingEmail) {
              setShowDeleteEmailModal(false);
            }
          }}
        >
          <div className="delete_account_modal" onClick={(e) => e.stopPropagation()}>
            <div className="delete_account_modal_header">
              <h2 className="delete_account_modal_title">Delete email?</h2>
              <button
                className="delete_account_modal_close"
                onClick={() => {
                  if (!isDeletingEmail) {
                    setShowDeleteEmailModal(false);
                  }
                }}
                aria-label="Close"
                disabled={isDeletingEmail}
              >
                <XIcon />
              </button>
            </div>

            <div className="delete_account_modal_content">
              <p className="delete_account_warning_text">
                Are you sure you want to remove <strong>{selectedEmail}</strong> from your account?
                You'll no longer be able to use this email address to log in or receive notifications.
              </p>

              <div className="delete_account_modal_actions">
                <button
                  className="delete_account_cancel_btn"
                  onClick={() => {
                    if (!isDeletingEmail) {
                      setShowDeleteEmailModal(false);
                    }
                  }}
                  disabled={isDeletingEmail}
                >
                  Cancel
                </button>
                <button
                  className="delete_account_confirm_btn"
                  onClick={handleDeleteEmail}
                  disabled={isDeletingEmail}
                  style={{
                    opacity: isDeletingEmail ? 0.6 : 1,
                    cursor: isDeletingEmail ? "not-allowed" : "pointer",
                  }}
                >
                  {isDeletingEmail ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Account Confirmation Modal */}
      {showDeleteAccountModal && (
        <div
          className="delete_account_modal_overlay"
          onClick={() => {
            if (!isDeletingAccount) {
              setShowDeleteAccountModal(false);
            }
          }}
        >
          <div className="delete_account_modal" onClick={(e) => e.stopPropagation()}>
            <div className="delete_account_modal_header">
              <h2 className="delete_account_modal_title">Delete account?</h2>
              <button
                className="delete_account_modal_close"
                onClick={() => {
                  if (!isDeletingAccount) {
                    setShowDeleteAccountModal(false);
                  }
                }}
                aria-label="Close"
                disabled={isDeletingAccount}
              >
                <XIcon />
              </button>
            </div>

            <div className="delete_account_modal_content">
              <p className="delete_account_warning_text">
                You'll no longer be able to use this account to log in, reset your password or receive
                notifications.
              </p>

              <div className="delete_account_modal_actions">
                <button
                  className="delete_account_cancel_btn"
                  onClick={() => {
                    if (!isDeletingAccount) {
                      setShowDeleteAccountModal(false);
                    }
                  }}
                  disabled={isDeletingAccount}
                >
                  Cancel
                </button>
                <button
                  className="delete_account_confirm_btn"
                  onClick={handleDeleteAccount}
                  disabled={isDeletingAccount}
                  style={{
                    opacity: isDeletingAccount ? 0.6 : 1,
                    cursor: isDeletingAccount ? "not-allowed" : "pointer",
                  }}
                >
                  {isDeletingAccount ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

